import { Dashboard } from '../../models/Dashboard'
import { Payment } from '../../models/Payment'
import { RecordFactory } from '../../models/RecordFactory'
import { Transaction } from '../../models/Transaction'
import { getViewTitleFromView } from '../../utils/dashboard'
import { currencyFormat } from '../../utils/numbers'
import { ComponentData } from '../../utils/typing'
import { PocketComponent, usePSComponent } from '../PocketComponent'
import { PocketComponentProps } from '../PocketComponent'
import { PSApplication } from '../app/PSApplication'
import {
    ClickableTableRow,
    Table,
    TableBody,
    TableCell,
    TableHead,
    TableHeader,
    TableRow,
} from '../app/design/Table'
import { DashboardSkeleton } from '../common/skeletons/DashboardSkeleton'
import { ClientTableCell } from './ClientTableCell'
import { EmptyDashboardLayout } from './EmptyDashboardLayout'
import { KeyMetric } from './KeyMetric'

type Props = {
    payments: Payment[]
    dashboard?: Dashboard
    searchText: string
    view: number
    isLoading: boolean
}

const getKeyMetric = (dashboard?: Dashboard) => {
    return [
        {
            title: `Amount`,
            value: currencyFormat(dashboard?.value ?? 0, true),
        },
        {
            title: `Payment(s)`,
            value: (dashboard?.count ?? 0).toString(),
        },
    ]
}

const viewWillAppear = async (base: PocketComponent<Props>) => {
    return await Promise.all(
        base.props.payments.map((p) => RecordFactory.getInstance().loadPayment(p.id!))
    )
}

const render = ({
    base: {
        props: { payments, dashboard, searchText, view, isLoading },
    },
}: PocketComponentProps<ComponentData<typeof viewWillAppear>, Props>) => {
    if (isLoading) {
        return <DashboardSkeleton />
    }

    const me = PSApplication.me()

    return (
        <div className="flex flex-col gap-6">
            <KeyMetric metrics={getKeyMetric(dashboard)} />
            <Table>
                <TableHeader>
                    <TableRow location="head">
                        <TableHead className="font-normal">To</TableHead>
                        {view === 0 && (
                            <TableHead className="font-normal">Deposited date</TableHead>
                        )}
                        <TableHead className="text-right font-normal">Amount</TableHead>
                    </TableRow>
                </TableHeader>
                <TableBody>
                    {payments.map((payment) => {
                        const isMe = payment.client?.id === me.id
                        const person = isMe
                            ? payment.employee
                                ? payment.employee
                                : payment.creator?.id !== me.id
                                  ? payment.creator
                                  : payment.vendor
                            : payment.client
                        const user = payment.recordType === 'payment' ? payment.vendor : person

                        const tx = payment as Transaction
                        const href = ((): string | null => {
                            switch (tx.recordType) {
                                case 'booking':
                                    return `/bookings/${tx.id}`
                                case 'invoice':
                                    return `/invoices/${tx.id}`
                                case 'package':
                                    return `/packages/${tx.id}`
                                case 'gift':
                                    return `/gifts/${tx.id}`
                                case 'payment':
                                    return `/payments/${tx.id}`
                                case 'estimate':
                                    return `/estimates/${tx.id}`
                                case 'charge':
                                    return `/charges/${tx.id}`
                                case 'subscription':
                                    return `/subscriptions/${tx.id}`
                                default:
                                    return null
                            }
                        })()

                        const Row = href ? ClickableTableRow : TableRow

                        return (
                            <Row key={payment.id} href={href ?? ''}>
                                <TableCell>
                                    <ClientTableCell user={user} subaccount={payment.subaccount} />
                                </TableCell>
                                {view === 0 && (
                                    <TableCell>
                                        {payment.depositedDate?.format('MMM D, YYYY')}
                                    </TableCell>
                                )}
                                <TableCell className="text-right">
                                    {currencyFormat(payment.amount, true)}
                                </TableCell>
                            </Row>
                        )
                    })}
                    {payments.length === 0 && (
                        <TableCell colSpan={10}>
                            <EmptyDashboardLayout dashboard={dashboard} searchtext={searchText} />
                        </TableCell>
                    )}
                </TableBody>
            </Table>
        </div>
    )
}

export const ExpensesDashboard = usePSComponent(render, viewWillAppear)
